/**
 * Partner Card Component
 *
 * Diamond Architecture: Presentation component for partner grid.
 * Displays partner logo, name, and role with link to profile.
 *
 * Used by:
 * - /partners (partners list page)
 */
import { Link } from '@/shared/i18n/routing';
import { routes } from '@letsgethome/routes';
import {
  partnerId as _toPartnerId,
  plainText,
  plaintextString,
  slug,
  type PartnerId,
  type S3Key,
} from '@letsgethome/types/branded';
import { S3Image } from '@letsgethome/ui/s3-image';

export type PartnerCardData = {
  id: PartnerId;
  slug: string;
  name: string;
  role: string;
  logoKey: S3Key;
};

type PartnerCardProps = {
  partner: PartnerCardData;
};

export function PartnerCard({ partner }: PartnerCardProps) {
  return (
    <Link
      href={routes.partners.detail(slug(partner.slug))}
      prefetch="intent"
      className="group flex flex-col items-center p-6 bg-background rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center"
    >
      {/* Logo */}
      <div className="relative w-24 h-24 rounded-full overflow-hidden mb-4">
        {partner.logoKey ? (
          <S3Image
            src={partner.logoKey}
            alt={plainText(partner.name)}
            fill
            className="object-cover"
            sizes="96px"
          />
        ) : (
          <div className="w-full h-full bg-primary/10 flex items-center justify-center">
            <span className="text-3xl font-bold text-primary">
              {plaintextString(partner.name.charAt(0))}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <h2 className="font-semibold text-foreground group-hover:text-primary transition-colors">
        {plaintextString(partner.name)}
      </h2>
      {partner.role && (
        <p className="mt-1 text-sm text-muted-foreground">{plaintextString(partner.role)}</p>
      )}
    </Link>
  );
}
